import { useUnit } from 'effector-react';
import { generateUuid } from '@lib/uuid';
import { blockToBlockIndex } from '@nx-frontend/shared/dtos';
import { useNavigateMenuItem } from '@nx-frontend/shared/hooks';
import { useChanges } from '@nx-frontend/shared/hooks/useChangeEvents';
import { scrollToBlockEvent } from '@nx-frontend/shared/store/customizer-controlbar-store';
import { $virtualNodePayload, VirtualNodePayload, generatePageFx } from '@nx-frontend/shared/store/virtual-portal-node';
import { MenuItem, PortalPersistentBlock, VirtualPortalPage } from '@nx-frontend/shared/types';
import { ChangeEventMeta, ChangeEventType } from '@nx-frontend/shared/types';

interface BlockCreatePayload {
  block: PortalPersistentBlock;
  batchId?: string;
  collectId?: string;
  meta?: Omit<Partial<ChangeEventMeta>, 'updatedAt' | 'userId'>;
  openPageAfterCreate?: boolean;
  scrollToBlock?: boolean;
  positionIndex?: number;
}

export const useBlockCreate = (): ((
  payload: BlockCreatePayload,
  pagePayloadParam?: VirtualNodePayload
) => Promise<{ node: VirtualPortalPage | null; menuItem: MenuItem | null; block: PortalPersistentBlock }>) => {
  const [pagePayloadStore, generatePage, scrollToBlock] = useUnit([
    $virtualNodePayload,
    generatePageFx,
    scrollToBlockEvent,
  ]);
  const setChanges = useChanges();
  const navigateMenuItem = useNavigateMenuItem();

  return async (payload, pagePayloadParam) => {
    const { block, batchId: externalBatchId, collectId, meta, openPageAfterCreate = false } = payload;

    let createdNode = null;
    let createdMenuItem = null;
    const pagePayload = pagePayloadParam ?? pagePayloadStore;

    if (pagePayload && meta?.pageId == null) {
      const { node, menuItem } = await generatePage({
        menuType: pagePayload.menuType,
        parentId: pagePayload.parentId,
        title: block.title,
        visible: pagePayload.visible,
      });

      createdNode = node;
      createdMenuItem = menuItem;

      const batchId = externalBatchId ?? generateUuid();
      setChanges({
        type: ChangeEventType.ADD_NODE,
        batchId,
        collectId,
        newValue: node,
        oldValue: undefined,
      });
      setChanges({
        type: ChangeEventType.ADD_MENU_ITEM,
        batchId,
        collectId,
        newValue: { menuType: pagePayload.menuType, item: menuItem },
        oldValue: undefined,
      });
      setChanges({
        type: ChangeEventType.ADD_BLOCK,
        batchId,
        collectId,
        newValue: block,
        oldValue: undefined,
        meta: {
          pageTitle: node.title,
          pageId: node.id,
          path: node.path,
        },
      });

      if (openPageAfterCreate) {
        navigateMenuItem({ path: menuItem.url, originalPath: menuItem.originalUrl });
      }
    } else {
      await setChanges({
        type: ChangeEventType.ADD_BLOCK,
        newValue: block,
        oldValue: undefined,
        batchId: externalBatchId,
        collectId,
        meta,
      });
    }
    if (payload.positionIndex !== undefined && payload.positionIndex >= 0) {
      setChanges({
        type: ChangeEventType.UPDATE_BLOCK_INDEX,
        newValue: { ...blockToBlockIndex(block), index: payload.positionIndex },
        oldValue: blockToBlockIndex(block),
        batchId: externalBatchId,
        collectId,
        meta,
      });
    }

    if (payload.scrollToBlock === true) {
      scrollToBlock(block);
    }

    return {
      node: createdNode,
      menuItem: createdMenuItem,
      block: block,
    };
  };
};
